import type { R7Mode } from 'Cloud/Application/types';
import store from 'lib/store';
import { xray } from 'lib/xray';
import { getCreateDocAPICall } from 'reactApp/api/CreateDocAPICall';
import { callDocumentIdAPI } from 'reactApp/api/DoucmentIdAPICall';
import { IS_B2B_BIZ_USER, IS_IOS_MOBILE, IS_PHONE_BROWSER, IS_PUBLIC, USER_EMAIL, userType } from 'reactApp/appHelpers/configHelpers';
import { DOCUMENTS_UNIQUE_IDS } from 'reactApp/appHelpers/featuresHelpers/features/documentsUniqueIds';
import { isDesktopR7App } from 'reactApp/appHelpers/settingsHelpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { ALL_DOCUMENTS_AVAILABLE_EXTENSIONS, DOCUMENT_TYPE_TO_URL_MAP } from 'reactApp/modules/allDocuments/allDocuments.constants';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { encodeId } from 'reactApp/modules/urlBuilder/encodeId';
import { WorkspaceSuperAppService } from 'reactApp/services';
import { sendKaktamLog, sendXray } from 'reactApp/utils/ga';
import { roundCustom } from 'reactApp/utils/helpers';

import { newDocNamesByExt } from './constants';

export const createWindowName = <T extends { id: string; home?: string }>(editorID: string, item: T) => {
    return `${editorID}_${(item.home || item.id).replace(/[^A-Za-z0-9]+/g, (found) => {
        const len = found.length;
        let i = 0,
            res = '';

        for (; i < len; i++) {
            res += found.charCodeAt(i);
        }

        return res;
    })}`;
};

export const joinPath = (...parts: string[]) =>
    parts
        .join('/')
        .replace(/\/{2,}/g, '/')
        .trim();

export const getCloudUrl = <T extends { id: string; storage?: string }>({ item, token }: { item: T; token?: string | null }) => {
    const itemStorage = item.storage === EStorageType.attaches || item.storage === EStorageType.viewerAttaches ? 'attaches' : 'home';
    const basePath = encodeId(joinPath(ROOT_FOLDER_ID, itemStorage, item.id)) ?? '';

    const searchParams = new URLSearchParams();

    if (token) {
        searchParams.set('token', token);
    }

    if (WorkspaceSuperAppService.isEnabled) {
        searchParams.set('superapp', '1');
    }

    const queryString = searchParams.toString();

    return queryString ? `${basePath}?${queryString}` : basePath;
};

export const getUrlV2 = <T extends { id: string; storage?: string }>(
    editor: { edit_uri?: string; id: string },
    { item, newExt, token }: { item: T; newExt?: string | null; token?: string | null }
) => {
    const isCreate = !!newExt;
    const itemId = item.id;
    if (isCreate) {
        return joinPath(ROOT_FOLDER_ID, itemId);
    }

    const cloudUrl = getCloudUrl({ item, token });
    const editUrl: string = editor.edit_uri ?? joinPath(editor.id);

    return joinPath(ROOT_FOLDER_ID, editUrl, cloudUrl);
};

export const getUniqueIdUrl = async ({ itemHome, publicId, ext }: { itemHome?: string; publicId?: string; ext?: string }) => {
    if (!itemHome && !publicId) {
        throw new Error('Either itemHome or publicId should be defined');
    }

    const { doc_id: docId } = await callDocumentIdAPI({ home: itemHome, public: publicId });

    const key = ext ? Object.entries(ALL_DOCUMENTS_AVAILABLE_EXTENSIONS).find(([_, value]) => value.includes(ext))?.[0] : null;

    return `/doc/${key ? DOCUMENT_TYPE_TO_URL_MAP[key] : 'ument'}/${docId}`;
};

export const getV2orUniqueIdUrl = async ({
    item,
    newExt,
    hasParentMountedOrShared,
    editorArg,
    isCreate,
}: {
    item: { home: string; ext: string; id: string; storage?: EStorageType };
    editorArg: any;
    newExt?: string | null;
    hasParentMountedOrShared: boolean;
    isCreate?: boolean;
}) => {
    const { isHome, isAllDocuments } = getStorage(item.storage ?? null);

    try {
        if (
            DOCUMENTS_UNIQUE_IDS &&
            Object.values(ALL_DOCUMENTS_AVAILABLE_EXTENSIONS).flat().includes(item.ext) &&
            !hasParentMountedOrShared &&
            (isCreate || isHome || isAllDocuments)
        ) {
            return await getUniqueIdUrl({ itemHome: item.home, ext: item.ext });
        }
    } catch (_) {
        // В случае ошибки продолжаем и пробуем открыть по ссылке в старом формате
    }

    return getUrlV2(editorArg, { item, newExt, token: null });
};

export const callApiCreateDoc = (path: string, ext: string, fileName?: string) =>
    getCreateDocAPICall().makeRequest({
        [IS_B2B_BIZ_USER ? 'path' : 'home']: joinPath(path, `${fileName ? fileName : newDocNamesByExt[ext]}.${ext}`),
    });

/** Функция, которая сокращает имя файла так, чтобы осталось видимым расширение. */
export const shortenFilename = <T extends { ext?: string; name: string }>(item: T, maxLength: number) => {
    const ext = item.ext ? `.${item.ext}` : '';
    const extLength = ext.length;
    let name = String(item.name);
    let nameLength = name.length;

    if (nameLength > maxLength) {
        const index = name.lastIndexOf(ext);

        if (index === nameLength - extLength - 1) {
            name = name.slice(0, index);
            nameLength = name.length;
            name = name.slice(0, nameLength - extLength - maxLength);

            if (nameLength > extLength) {
                return `${name}…\u00A0${ext}`;
            }

            return `${name}…`;
        }

        return `${name.slice(0, maxLength)}…`;
    }

    return name;
};

export const getExt = (item: any | string) => {
    let ext = '';

    if (typeof item === 'object') {
        ext = item.ext;
    } else {
        ext = String(item);
        const index = ext.lastIndexOf('.');
        if (index !== -1) {
            ext = ext.slice(Math.max(0, index + 1));
        }
    }

    return ext && ext.toLowerCase();
};

export const getIsOpenDocumentWarning = (hasSharedEditor: boolean) => {
    if (hasSharedEditor) {
        return false;
    }

    return store.get(`${USER_EMAIL}|openDocumentWarning`);
};

export const setIsOpenDocumentWarning = (hasSharedEditor: boolean) => {
    if (hasSharedEditor) {
        store.remove(`${USER_EMAIL}|openDocumentWarning`);
        return;
    }

    store.set(`${USER_EMAIL}|openDocumentWarning`, true);
};

/** Проверяет не закрыто ли окно или вкладка. */
export const isOpenedTab = (tab: any) => {
    try {
        // В IE9 можно получить exception.
        return !tab.closed && tab.opener;
    } catch (error) {
        return false;
    }
};

export const getGroupVal = (ms: number) =>
    roundCustom(
        Math.round(ms),
        [0, 1000, 5000, 10000, 15000, 20000, 30000, 60000],
        ['lt_1s', 'gt_1s', 'gt_5s', 'gt_10s', 'gt_15s', 'gt_20s', 'gt_30s', 'gt_1m']
    );

const R7_LOGGER_PREFIX = `r7-edtr-${IS_PUBLIC ? 'public' : 'home'}` as const;

/**
 * Отправляет радар о жизни Р7
 *
 * Если мы в тачовом браузере - дополнительно отправляет радар с мобильным префиксом
 */
export const sendR7LifecycleRadar = (name: string, iData?: string) => {
    sendXray(`${R7_LOGGER_PREFIX}-${name}`, iData);
    if (IS_PHONE_BROWSER) {
        sendXray(`m-${R7_LOGGER_PREFIX}-${name}`, iData);
    }
};

/**
 * Отправляет лог в кактам в файл ````${R7_LOGGER_PREFIX}_log````
 * @param data данные, которые полетят в кактам
 */
export const sendR7KaktamLog = (data: any) => sendKaktamLog(data, `${R7_LOGGER_PREFIX}_log`);

/**
 * Отправляет радар о скорости работы Р7
 */
export const sendR7LoadTimeRadar = (name: string, groupVal: number, loadTime: number, withLoadingApi: number = 0) => {
    sendXray(`${R7_LOGGER_PREFIX}_${name}`, {
        [`chart_${groupVal}`]: 1,
        ms: loadTime,
        withApiLoad: withLoadingApi,
    });
};

/**
 * Время от начала инициализации до загрузки редактора [user_type юзеры]
 */
export const sendR7LoadTimeRadarWithUserType = (name: string, groupVal: number) => {
    sendXray(`${R7_LOGGER_PREFIX}_${name}_chart_${groupVal}_${userType}`);
};

export const sendR7Radar = ({ mode, xrayItemType, fileExtension }: { mode?: R7Mode; xrayItemType: string | null; fileExtension }) => {
    let iString = 'desktop';
    const isDesktopR7 = isDesktopR7App();

    if (IS_PHONE_BROWSER) {
        iString = 'phone';
    } else if (isDesktopR7) {
        iString = 'desktop_r7';
    }

    const radarData = {
        i: iString,
        dwh: {
            current_email: USER_EMAIL || undefined,
            is_phone_browser: IS_PHONE_BROWSER,
            platform: IS_PHONE_BROWSER ? (IS_IOS_MOBILE ? 'ios' : 'android') : undefined,
            file_ext: fileExtension,
            mode,
            item_type: xrayItemType,
            desktop_r7: isDesktopR7,
        },
    };

    // Старые радары, пока не перейдем на новый - не убираем
    switch (mode) {
        case 'edit':
            xray.send(USER_EMAIL ? 'r7-editing-load-ok' : 'r7-editing-anon-load-ok', radarData);
            break;

        case 'comment':
            xray.send(USER_EMAIL ? 'r7-commenting-load-ok' : 'r7-commenting-anon-load-ok', radarData);
            break;
    }

    xray.send(`r7-init${xrayItemType ? `-${xrayItemType}` : ''}${mode ? `-${mode}` : ''}`, radarData);
};
